import {
  BoxProps,
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { useAssignRestaurantSupplierApi, useGetAvailableRestaurantsApi } from 'apis/restaurant-suppliers';
import React, { useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

type FormValues = {
  restaurant: { value: number; label: string } | null;
  favorite: boolean;
  isGpoRelation: boolean;
};

const defaultValues: FormValues = {
  restaurant: null,
  favorite: true,
  isGpoRelation: false,
};

export interface IAssignRestaurantModalCell extends BoxProps {
  trigger: JSX.Element;
}

export const AssignRestaurantModalCell = ({ trigger }: IAssignRestaurantModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { supplierId } = useParams<{ supplierId: string }>();
  const { loadOptions } = useGetAvailableRestaurantsApi();
  const loadRestaurantOptions = useMemo(() => loadOptions(parseInt(supplierId)), [loadOptions, supplierId]);
  const { isLoading: isAssignLoading, mutate: assignRestaurant } = useAssignRestaurantSupplierApi();

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const favoriteValue = watch('favorite');
  const isGpoRelationValue = watch('isGpoRelation');

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (!data.restaurant?.value) return;

    const variables = {
      body: {
        restaurantId: data.restaurant.value,
        supplierId: parseInt(supplierId),
        favorite: data.favorite,
        isGpoRelation: data.isGpoRelation,
      },
    };

    assignRestaurant(variables, {
      onSuccess: () => {
        onClose();
        reset(defaultValues);
      },
    });
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        onCloseComplete={() => reset(defaultValues)}
        closeOnOverlayClick={false}
        isCentered={false}
        size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack spacing='xs' align='start'>
              <Heading as='h3' size='lg'>
                Assign Restaurant
              </Heading>
              <Text color='gray.500' fontSize='md' fontWeight='normal'>
                Search a restaurant and set its flags before assigning.
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack as='form' id='assignRestaurantForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                control={control}
                name='restaurant'
                rules={{ required: 'Restaurant is required' }}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                  <FormControl isInvalid={!!error} isRequired>
                    <FormLabel>Restaurant</FormLabel>
                    <ChakraAsyncSelect
                      ref={ref}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      loadOptions={loadRestaurantOptions}
                      placeholder='Search and select a restaurant...'
                      isClearable
                      noOptionsMessage={({ inputValue }: { inputValue: string }) =>
                        inputValue ? 'No restaurants found' : 'Type to search restaurants'
                      }
                      loadingMessage={() => 'Loading restaurants...'}
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <FormControl display='flex' alignItems='center'>
                <Switch
                  id='assign-favorite'
                  isChecked={!!favoriteValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('favorite', e.target.checked)}
                />
                <FormLabel htmlFor='assign-favorite' mb='0' ms='sm' fontWeight='normal'>
                  Favorite
                </FormLabel>
              </FormControl>

              <FormControl display='flex' alignItems='center'>
                <Switch
                  id='assign-gpo'
                  isChecked={!!isGpoRelationValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('isGpoRelation', e.target.checked)}
                />
                <FormLabel htmlFor='assign-gpo' mb='0' ms='sm' fontWeight='normal'>
                  GPO relation
                </FormLabel>
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='ghost' colorScheme='gray' onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='assignRestaurantForm'
                isLoading={isAssignLoading}>
                Assign
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
